import type { ReactElement, ReactNode } from "react";
import { layout, space } from "@tokens/index";
import { Text } from "@primitives/Text";

/**
 * PageHeader: the heading block at the top of a page column.
 *
 * A display-face `h1`, an optional muted description capped at the prose
 * measure, and an optional trailing actions slot that wraps under the title
 * on narrow columns. Admin and settings pages share this one grammar.
 */

export interface PageHeaderProps {
    readonly title: string;
    readonly description?: string;
    readonly actions?: ReactNode;
}

export function PageHeader({ title, description, actions }: PageHeaderProps): ReactElement {
    return (
        <header
            style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: space[3],
            }}
        >
            <div style={{ display: "grid", gap: space[2], minWidth: 0, flex: "1 1 240px" }}>
                <Text as="h1" role="display" style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                    {title}
                </Text>
                {description !== undefined && (
                    <Text
                        as="p"
                        role="body"
                        tone="muted"
                        style={{ maxWidth: layout.proseMaxWidth }}
                    >
                        {description}
                    </Text>
                )}
            </div>
            {actions}
        </header>
    );
}
